import { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Grid,
  GridItem,
  Badge,
  SimpleGrid,
  Button,
  Spinner,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import MeetingCard, { Meeting } from './MeetingCard'
import { glassCard } from '../styles'
import { apiFetch } from '../utils/apiFetch'
import { usePollContext } from '../contexts/PollContext'

const Dashboard = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(() => {
    const data = localStorage.getItem('user')
    return data ? JSON.parse(data) : null
  })
  const [meetings, setMeetings] = useState<Meeting[]>([])
  const [meetingsLoading, setMeetingsLoading] = useState(true)
  const [dismissingPolls, setDismissingPolls] = useState(false)
  const [myStats, setMyStats] = useState<{
    tastingsAttended: number
    uniqueBeersTasted: number
    ratingsGiven: number
    avgRating: number | null
  } | null>(null)
  const { pendingCount: pendingPollCount, refresh: refreshPolls } = usePollContext()

  const isAdmin: boolean = user?.isAdmin ?? false

  const monthsInClub = (joinDate?: string): number | null => {
    if (!joinDate) return null
    const joined = new Date(joinDate)
    const now = new Date()
    return (now.getFullYear() - joined.getFullYear()) * 12 + (now.getMonth() - joined.getMonth())
  }

  const profileMonths = monthsInClub(user?.joinDate)
  const profileTitle = user?.title || 'Beer Enthusiast'
  const profileBadge = profileMonths !== null
    ? `${profileTitle} • ${profileMonths} months in club`
    : profileTitle

  // Fetch fresh profile from server so title/joinDate are always current
  useEffect(() => {
    apiFetch('/api/auth/me')
      .then(r => r.json())
      .then(data => {
        const updated = { ...user, ...data }
        localStorage.setItem('user', JSON.stringify(updated))
        setUser(updated)
      })
      .catch(() => { })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    apiFetch('/api/meetings')
      .then(r => r.json())
      .then(data => { setMeetings(data); setMeetingsLoading(false) })
      .catch(() => setMeetingsLoading(false))
  }, [])

  useEffect(() => {
    apiFetch('/api/beers/my-stats')
      .then(r => r.json())
      .then(data => setMyStats(data))
      .catch(() => { })
  }, [])

  const handleUpdate = (updated: Meeting) => {
    setMeetings(prev => prev.map(m => m._id === updated._id ? updated : m))
  }

  const handleDismissAllPolls = async () => {
    setDismissingPolls(true)
    await apiFetch('/api/polls/dismiss-all', { method: 'POST' })
    refreshPolls()
    setDismissingPolls(false)
  }

  const recentMeetings = meetings.slice(0, 5)

  return (
    <Box minH="100vh" position="relative">

      <Box p={{ base: 4, md: 8 }} pt={{ base: 16, md: 20 }}>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8} maxW="1200px" mx="auto">

          <GridItem colSpan={{ base: 1, lg: 2 }}>
            <Box {...glassCard} borderRadius="2xl" p={{ base: 5, md: 8 }} textAlign="center">
              <VStack spacing={4}>
                <Avatar size="xl" src={user?.picture} name={user?.name} border="4px solid" borderColor="whiteAlpha.300" />
                <VStack spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" color="white">
                    Welcome back, {user?.name}! 🍺
                  </Text>
                  <Text fontSize="md" color="whiteAlpha.700">
                    Ready for another mystery beer adventure with Bröldraskapet?
                  </Text>
                  <Badge colorScheme="yellow" fontSize="sm" px={3} py={1}>
                    {profileBadge}
                  </Badge>
                </VStack>
              </VStack>
            </Box>
          </GridItem>

          {pendingPollCount > 0 && (
            <GridItem colSpan={{ base: 1, lg: 2 }}>
              <Box {...glassCard} borderRadius="xl" p={4} borderColor="orange.400">
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <HStack spacing={3}>
                    <Text fontSize="xl">🗳️</Text>
                    <Text color="white" fontWeight="semibold">
                      {pendingPollCount} vote{pendingPollCount > 1 ? 's' : ''} waiting for you
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Button size="sm" variant="ghost" color="whiteAlpha.500" isLoading={dismissingPolls}
                      onClick={handleDismissAllPolls}>
                      Dismiss all
                    </Button>
                    <Button size="sm" colorScheme="orange" variant="ghost" onClick={() => navigate('/votes')}>
                      Go vote →
                    </Button>
                  </HStack>
                </HStack>
              </Box>
            </GridItem>
          )}

          <GridItem>
            <VStack spacing={4} align="stretch">
              <Text fontSize="xl" fontWeight="bold" color="white">Your Beer Journey 🍻</Text>
              {myStats === null ? (
                <Spinner color="white" />
              ) : (
                <SimpleGrid columns={2} spacing={4}>
                  {[
                    { value: myStats.tastingsAttended, label: 'Tastings attended' },
                    { value: myStats.uniqueBeersTasted, label: 'Unique beers tasted' },
                    { value: myStats.ratingsGiven, label: 'Beers rated' },
                    { value: myStats.avgRating != null ? `★ ${myStats.avgRating.toFixed(2).replace(/\.?0+$/, '')}` : '—', label: 'Avg rating' },
                  ].map(({ value, label }) => (
                    <Box key={label} {...glassCard} p={4} borderRadius="lg" textAlign="center">
                      <Text fontSize="2xl" fontWeight="bold" color="white">{value}</Text>
                      <Text fontSize="sm" color="whiteAlpha.700">{label}</Text>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </VStack>
          </GridItem>

          <GridItem>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" align="center">
                <Text fontSize="xl" fontWeight="bold" color="white">Recent Meetings</Text>
                <Button size="xs" variant="ghost" color="whiteAlpha.600" _hover={{ color: 'white' }} onClick={() => navigate('/meetings')}>
                  View all →
                </Button>
              </HStack>
              {meetingsLoading ? (
                <Spinner color="white" />
              ) : (
                <VStack spacing={3} align="stretch">
                  {recentMeetings.map(meeting => (
                    <MeetingCard
                      key={meeting._id}
                      meeting={meeting}
                      isAdmin={isAdmin}
                      currentUserId={user?._id}
                      onUpdate={handleUpdate}
                    />
                  ))}
                </VStack>
              )}
            </VStack>
          </GridItem>

        </Grid>
      </Box>
    </Box>
  )
}

export default Dashboard
